/**
 * Decide when a mutation should call the `*-with-sync` companion endpoint vs the legacy variant.
 *
 * Per the Incident ↔ Kanban Workspaces integration guide:
 *
 *   Calling the legacy `PATCH /incidents/{id}` or `POST /kanban/cards/{cardId}/move`
 *   does NOT trigger sync, even if a link's flags are on. Propagation only runs when callers
 *   use the `*-with-sync` companion endpoints.
 *
 * The frontend rule: route through the `*-with-sync` endpoint only when at least one
 * bridge link for the entity has at least one `sync_*` flag enabled. Otherwise prefer
 * the legacy endpoint. Calling `with-sync` when no flag is on is harmless server-side
 * (the propagation is a no-op) but adds latency and noise; we avoid it for clarity.
 */

import type { IncidentKanbanLink } from '../types/incident';

export function anySyncFlagOn(link: IncidentKanbanLink): boolean {
  return (
    link.sync_status === 1 ||
    link.sync_priority === 1 ||
    link.sync_assignees === 1 ||
    link.sync_comments === 1 ||
    link.propagate_watchers === 1
  );
}

/**
 * @returns `true` when at least one bridge link for the incident has at least one sync flag set.
 *          When `links` is `null`/`undefined`/empty, returns `false` (no propagation possible).
 */
export function shouldUseSyncForIncident(
  links: IncidentKanbanLink[] | null | undefined,
): boolean {
  if (!links || links.length === 0) return false;
  return links.some(anySyncFlagOn);
}

/**
 * Card-side variant. Accepts the prefetched bridge link list filtered to the card_id
 * (caller owns the fetch + filter). Returns `true` if any of those links have any
 * sync flag set.
 *
 * For the card-detail panel we don't always have per-link flag knowledge cheaply;
 * callers may instead pass `useSync = (linkedIncidents.length > 0)` as a coarse
 * proxy. The backend short-circuits when no flag is set, so the proxy is safe.
 */
export function shouldUseSyncForCard(
  links: IncidentKanbanLink[] | null | undefined,
): boolean {
  return shouldUseSyncForIncident(links);
}
